import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import {
    ApplicationFacade,
    FavoritesFacade,
    FavoritesService,
    FavoriteTicket,
    SessionService,
    ShoppingCartService
} from '@ridango/components';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
import { CartTicketRequest } from '@ridango/components/lib/purchase/purchase.model';
import { Router } from '@angular/router';
import { EditTicketFavoritesModalComponent } from './edit-ticket-favorites-modal/edit-ticket-favorites-modal.component';
import { ModalController } from '@ionic/angular';
import { forkJoin } from 'rxjs';
import { ScreenOrientationService } from '@ridango/core/lib/services/screenOrientation.service';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';

@Component({
    selector: 'gl-purchase-v2-favorite-tickets',
    templateUrl: './favorite-tickets.component.html',
    styleUrls: ['./favorite-tickets.component.scss']
})
export class FavoriteTicketsComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() favoriteTickets: FavoriteTicket[] = [];
    public assetsPath$ = this.applicationFacade.assetsPath$;
    public currentLanguage = this.translate.currentLang;
    public isInLandscapeMode = this.screenOrientationService.isInLandscapeMode;

    private ticketAdding = false;
    private readonly BUY_FAVORITE_TICKET = 'Buy favorite ticket';

    constructor(
        private favoritesFacade: FavoritesFacade,
        private favoritesService: FavoritesService,
        private applicationFacade: ApplicationFacade,
        public translate: TranslateService,
        private sessionService: SessionService,
        private shoppingCartService: ShoppingCartService,
        private router: Router,
        private modalCtrl: ModalController,
        private screenOrientationService: ScreenOrientationService,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });
    }

    onTicketClick(ticket) {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: this.BUY_FAVORITE_TICKET
        });

        if (this.ticketAdding) {
            return;
        }

        this.ticketAdding = true;

        const payload: CartTicketRequest = {
            endZoneId: ticket.endZoneId || null,
            startZoneId: ticket.startZoneId || null,
            productId: ticket.productId.toString(),
            email: this.sessionService.getLoggedInEmail()
        };
        const cartId = this.shoppingCartService.getCartId();

        this.shoppingCartService
            .addProductToCartV2({ shoppingCartId: cartId || null, tickets: [payload] })
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart']);
                    this.ticketAdding = false;
                },
                (error) => {
                    this.ticketAdding = false;

                    if (error.status === 409) {
                        this.shoppingCartService.setCartId(null);
                        this.onTicketClick(ticket);
                    }
                }
            );
    }

    async editFavoriteTickets() {
        const modal = await this.modalCtrl.create({
            component: EditTicketFavoritesModalComponent,
            cssClass: 'edit-ticket-favorites-modal',
            componentProps: { favoriteTickets: this.favoriteTickets }
        });

        await modal.present();

        modal.onDidDismiss().then(async ({ data }) => {
            if (data) {
                forkJoin(data.map((productId) => this.favoritesService.deleteFavoriteTickets(productId))).subscribe(
                    () => this.favoritesFacade.getFavoriteTickets()
                );
            }
        });
    }
}
